import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import {
  hasAnyPermission,
  hasPermission,
  isAdmin,
  type CurrentUser,
  type PermissionCode,
  type RoleCode,
} from '@dsv/shared';
import { authApi } from '@/api/auth.api';
import { tokenStorage } from '@/api/token-storage';
import { testViewApi } from '@/api/auth.api';
import { RequestError } from '@/api/client';

import { setLocale } from '@/i18n';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<CurrentUser | null>(null);
  /** 是否已尝试过恢复会话（刷新页面后用本地令牌拉 /auth/me） */
  const initialized = ref(false);
  /**
   * 会话恢复失败了，但**令牌还在**——即后端/数据库暂时不可用，不是登录过期。
   * 登录页据此提示「登录状态还在，服务恢复后可直接重试」，不用重新输密码。
   */
  const restoreFailed = ref(false);
  const esiEnabled = ref(false), esiAvailable = ref(false), esiError = ref('');
  async function refreshEsiStatus() {
    const status = await authApi.esiStatus();
    esiEnabled.value = status.enabled; esiAvailable.value = status.available;
    return status;
  }
  async function loginEsi(): Promise<void> {
    const res = await authApi.esiLogin();
    tokenStorage.exitTestView();
    tokenStorage.set(res.accessToken, res.refreshToken);
    sessionStorage.removeItem('ds-esi-signed-out');
    user.value = res.user;
    esiError.value = '';
    initialized.value = true;
    setLocale(res.user.locale);
  }

  const isLoggedIn = computed(() => user.value !== null);
  const permissions = computed<PermissionCode[]>(() => user.value?.permissions ?? []);
  /** 一人一角色（2026-09-22）。没分配角色时为 null，此时没有任何权限点。 */
  const role = computed<RoleCode | null>(() => user.value?.role ?? null);
  const isAdministrator = computed(() => isAdmin(role.value));

  /** 权限判断走 @dsv/shared 的实现，和后端鉴权用的是同一套逻辑 */
  function can(...required: PermissionCode[]): boolean {
    return isAdministrator.value || hasPermission(permissions.value, required);
  }

  function canAny(...required: PermissionCode[]): boolean {
    return isAdministrator.value || hasAnyPermission(permissions.value, required);
  }

  async function login(username: string, password: string): Promise<void> {
    const res = await authApi.login({ username, password });
    tokenStorage.exitTestView();
    tokenStorage.set(res.accessToken, res.refreshToken);
    user.value = res.user;
    initialized.value = true;
    setLocale(res.user.locale);
  }

  /** 页面刷新后恢复会话。本地没有令牌就直接标记为已初始化，不发请求。 */
  async function restore(): Promise<void> {
    if (initialized.value) return;

    restoreFailed.value = false;
    // Refresh even with a cached DS token so expired sessions retain the QR login entry.
    const status = await refreshEsiStatus().catch(() => null);

    if (!tokenStorage.getAccess()) {
      try {
        if (status?.available && sessionStorage.getItem('ds-esi-signed-out') !== 'true') await loginEsi();
      } catch (error) {
        esiError.value = error instanceof RequestError ? error.localizedMessage : '';
      }
      initialized.value = true;
      return;
    }

    try {
      user.value = await authApi.me();
    } catch {
      user.value = null;
      // **这里不清令牌。** 令牌真的失效时，axios 拦截器已经在 401 分支清掉了；
      // 走到这里令牌还在，说明是服务不可用或断网 —— 那种情况下清令牌等于
      // 因为后端抖动一下就把人踢回登录页重输密码。2026-09-22 数据库短暂连不上时真实发生过。
      restoreFailed.value = tokenStorage.getAccess() !== null;
      if (!restoreFailed.value && status?.available && sessionStorage.getItem('ds-esi-signed-out') !== 'true') {
        try { await loginEsi(); }
        catch (error) { esiError.value = error instanceof RequestError ? error.localizedMessage : ''; }
      }
    } finally {
      initialized.value = true;
    }
  }

  /** 服务恢复后重试一次会话恢复，供登录页的「重试」用 */
  async function retryRestore(): Promise<void> {
    initialized.value = false;
    await restore();
  }

  /** 返回页面/业务导航时重读当前授权；避免停用品牌后继续显示旧资料。 */
  let profileRefresh: Promise<void> | null = null;
  function refreshProfile(): Promise<void> {
    if (!user.value || !tokenStorage.getAccess()) return Promise.resolve();
    const identity = user.value.id;
    profileRefresh ??= authApi.me().then((current) => { if (user.value?.id === identity && tokenStorage.getAccess()) user.value = current; }).finally(() => { profileRefresh = null; });
    return profileRefresh;
  }

  async function logout(): Promise<void> {
    sessionStorage.setItem('ds-esi-signed-out', 'true');
    void refreshEsiStatus().catch(() => undefined);
    const refreshToken = tokenStorage.adminRefresh();
    if (refreshToken) {
      // 通知后端吊销 refresh token；失败也不阻塞本地登出
      await authApi.logout(refreshToken).catch(() => undefined);
    }
    if (import.meta.env.VITE_PLM_STANDALONE === 'true') {
      await fetch('/_esi/logout', { method: 'POST', credentials: 'same-origin' }).catch(() => undefined);
    }
    tokenStorage.exitTestView();
    clearSession();
  }

  /** 本地清理会话，不发请求。令牌过期被动登出时由 axios 拦截器调用。 */
  function clearSession(): void {
    tokenStorage.clear();
    user.value = null;
    restoreFailed.value = false;
  }

  return {
    user,
    initialized,
    restoreFailed,
    esiEnabled, esiAvailable, esiError, loginEsi,
    isLoggedIn,
    permissions,
    role,
    isAdministrator,
    can,
    canAny,
    login,
    restore,
    retryRestore,
    refreshProfile,
    logout,
    clearSession,
    async enterTestView(userId: string) {
      const res = await testViewApi.start(userId);
      tokenStorage.enterTestView(res.accessToken);
      user.value = res.user;
    },
    exitTestView() { tokenStorage.exitTestView(); },
  };
});
